/**
 * English strings for the post-checkout onboarding tour / setup guide (PREC-3700).
 * Kept out of en.ts to keep the main translation file within the size gate.
 */
import { enOnboardingTourScoringSteps } from "./enOnboardingTourScoringSteps";
import { enOnboardingTourScoring } from "./enOnboardingTourScoring";
import { enOnboardingTourReview } from "./enOnboardingTourReview";
import { enOnboardingTourListTour } from "./enOnboardingTourListTour";

export const enOnboardingTour = {
	eyebrow: "Step {{current}} of {{total}}",
	placeholderNote: "This part of the guide is on its way — hit Continue for now.",
	rail: {
		setupGuide: "Setup guide",
		welcomeTour: "Welcome tour",
		review: "Review",
		waitingForData: "Available once the setup finishes",
	},
	loading: {
		title: "I'm currently {{verb}}…",
		body: "This usually takes a minute or two. You can wait until it's done, or continue with the other sections in the setup guide — I'll keep working in the background and come back to this when it's ready.",
		hint: "This step will show a Review arrow in the left menu the moment it's ready.",
		verbs: {
			building: "building your Knowledge Base",
			reScoring: "re-scoring your leads",
			analyzing: "analyzing your leads",
		},
	},
	footer: {
		skip: "Skip the onboarding",
		skipInvited: "Skip the tour",
		back: "Back",
		continue: "Continue",
		next: "Next: {{title}}",
		finish: "Finish",
	},
	skipModal: {
		title: "Leave setup for now?",
		body: "Your enrichment keeps running in the background — you can pick up where you left off any time.",
		// Before "Check & activate" the configured lists are still paused (PREC-4490).
		bodyListsWaiting_one:
			"Your list stays paused until you finish the guide, so it runs with your lead scoring in place. You can pick up where you left off any time.",
		bodyListsWaiting_other:
			"Your {{count}} lists stay paused until you finish the guide, so they run with your lead scoring in place. You can pick up where you left off any time.",
		keepGoing: "Never mind, keep going",
		skipForNow: "Just skip for now",
	},
	nudge: {
		title: "We're still working on this…",
		body: "This is taking a little longer than usual. We'll notify you the moment it's ready. In the meantime — want to explore a bit more of the Signalist platform?",
		keepWaiting: "Keep waiting",
		explore: "Sure, let's explore",
	},
	finishModal: {
		title: "You're all set 🎉",
		subtitle: "Here's what's ready for you:",
		subtitleRunning: "Here's where things stand — the rest keeps running in the background:",
		rows: {
			lists: {
				done: "Your lists are built and scored",
				running: "Your lists are still being built and scored",
			},
			knowledge: {
				done: "Your Knowledge Base is ready",
				running: "We're building your Knowledge Base",
			},
			research: {
				done: "Your Deep Research prompt is ready",
				running: "We're writing your Deep Research prompt",
			},
			sender: {
				done: "Your sender is connected",
				running: "No sender connected yet — connect one to start reaching out",
			},
		},
		exploreLists: "Explore my lists",
	},
	dashboard: {
		title: "Finish setting up Signalist",
		progress: "{{done}} of {{total}} tasks complete · {{remaining}} to go",
		learnMore: "Learn more",
		pills: {
			generating: "Generating…",
			resultReady: "Result ready",
		},
		rows: {
			createLists: { title: "Create your first lists", action: "Create" },
			profileTracker: { title: "Set up your first Profile Tracker", action: "Set up" },
			leadScoring: { title: "Review Lead Scoring", action: "Review" },
			connectSenders: { title: "Connect senders", action: "Connect" },
			deepResearch: { title: "Review Deep Research", action: "Review" },
			knowledge: { title: "Review Knowledge Database", action: "Review" },
			firstCampaign: { title: "Create your first campaign", action: "Create" },
			reachOut: { title: "Reach out to the first leads", action: "Open lists" },
			crm: { title: "Connect Signalist to your CRM", action: "Connect" },
		},
		resumeBanner: {
			title: "You're almost set up",
			subtitle_one: "{{count}} step left · pick up where you left off",
			subtitle_other: "{{count}} steps left · pick up where you left off",
			listsWaiting_one:
				"Your list starts as soon as you finish the guide, so it runs with your lead scoring in place.",
			listsWaiting_other:
				"Your {{count}} lists start as soon as you finish the guide, so they run with your lead scoring in place.",
			cta: "Continue onboarding",
			dismiss: "Dismiss",
		},
	},
	sidebar: {
		title: "Setup guide",
		remaining_one: "{{count}} step left",
		remaining_other: "{{count}} steps left",
	},
	readyBar: {
		scoring: "Your Lead Scoring is ready",
		deepResearch: "Your Deep Research prompt is ready",
		knowledge: "Your Knowledge Base is ready",
		knowledgeFailed: "Your Knowledge Base could not be built.",
		retry: "Try again",
		cta: "Review now",
		dismiss: "Hide this reminder",
	},
	workspaceGuard: {
		kinds: {
			scoring: "Lead scoring & segments",
			deepResearch: "The Deep Research prompt",
			knowledge: "The Knowledge Base",
		},
		banner: "{{what}} is a shared workspace setting, already configured by your team.",
		bannerAdmin: "You can change it, but it changes for everyone.",
		bannerUser: "Only a Company Admin can change it.",
		roleAdmin: "Company Admin",
		roleUser: "User",
		confirmTitle: "Change a workspace setting?",
		confirmBody:
			"{{what}} is shared across your workspace. Saving affects everyone in your workspace — your teammates aren't notified.",
		confirmCta: "Yes, change for everyone",
		cancel: "Cancel",
		blockTitle: "Only Company Admins can change this",
		blockBody:
			"{{what}} is a shared workspace setting and your account is a User. Ask a Company Admin if something should be changed.",
		close: "Close",
	},
	scoringStep: enOnboardingTourScoring,
	reviewSteps: enOnboardingTourReview,
	listTour: enOnboardingTourListTour,
	createKnowledge: {
		intro: {
			title: "Let's create your Knowledge Base",
			subtitle:
				"This is the source your AI copy draws on. Here's what goes into it — and you can edit all of it later.",
			rows: {
				painPoints: {
					title: "Pain Points & Value Propositions",
					desc: "Built for each persona separately — a CEO has different pain points and expects a different impact than an operational lead, so your messaging speaks to each one.",
				},
				products: {
					title: "Products & Services",
					desc: "We pull this straight from your website, so you don't start from a blank page.",
				},
				caseStudies: {
					title: "Case Studies",
					desc: "Also pulled from your website — the proof points you already show off.",
				},
			},
			callout:
				"No need to get this perfect. We also pull extra context from your website, and everything here stays editable after setup. The next few questions just let you confirm our assumptions.",
			start: "Start — a few quick questions",
		},
		chat: {
			intro:
				"Let's set up your Knowledge Base — this helps us write messages like you would do it yourself.",
			closing:
				"Thanks, that's really helpful. I'll now draft the full Knowledge Base for every persona — you can review it later.",
			looksRight: "Looks right",
			edit: "Edit",
			cancel: "Cancel",
			save: "Save",
			confirmed: "Confirmed",
		},
		questions: {
			products: {
				q: "What are your core products or services?",
				hint: "You may have one offering for everyone, or different offerings per ICP — edit whichever fits.",
			},
			painPoints: { q: "What are the core pain points your clients want solved?", hint: "" },
			valueProp: {
				q: "What makes your approach different — your core value proposition?",
				hint: "",
			},
			references: {
				q: "What are your best references and case studies you typically mention?",
				hint: "",
			},
		},
		pending: {
			title: "Preparing your assumptions…",
			body: "I'm drafting a few starting points from your website and what you told us. This usually takes a moment.",
		},
		build: "Build my Knowledge Base",
		confirmAllHint: "Confirm all four to build your Knowledge Base",
	},
	steps: {
		howItWorks: {
			title: "How Signalist works",
			desc: "Three stages take you from raw signals to booked conversations.",
			railNote:
				"Let me show you how Signalist works. Watch the pipeline cycle through its stages — click any tab to take a closer look.",
			railNoteInvited:
				"Welcome to your team's workspace! Here's a quick look at how Signalist turns signals into conversations.",
			continueStage2: "Next: Segment & score",
			continueStage3: "Next: Reach out",
		},
		scoring: {
			title: "Score your leads",
			desc: "Company and Persona segments rank every lead for you.",
			railNote:
				"While you were signing up, I already built your first scoring model. Let's have a look at your segments.",
			railNoteInvited:
				"Your team already set up scoring. Here's how leads get ranked in this workspace.",
		},
		...enOnboardingTourScoringSteps,
		createKnowledge: {
			title: "Create Knowledge Base",
			desc: "Confirm a few assumptions and we'll build the source your AI copy draws on.",
			railNote:
				"This is the source your AI copy draws on — pain points, value props, products and case studies. Confirm a few assumptions and we'll build it, pulling extra context from your website.",
		},
		connect: {
			title: "Connect your sender",
			desc: "Connect the account you'll reach out from — LinkedIn, email, or both.",
			railNote:
				"To send messages from Signalist you need a connected sender. It only takes a minute — and you can add email later, too.",
			railNoteInvited:
				"If you'll be sending messages yourself, connect your accounts here — otherwise just continue.",
			intro: "Connect the account you'll send from. One sender can use both LinkedIn and email.",
			linkedinTitle: "LinkedIn",
			linkedinDescription: "Send connection requests and messages from your LinkedIn account.",
			emailTitle: "Email",
			emailDescription: "Send and receive emails directly from your inbox.",
			connectNow: "Connect now",
			pillConnected: "Connected",
			pillNotConnected: "Not connected",
			pillNotAvailable: "Not available",
			connectedSummary: "Sending limits and sync run with safe defaults.",
			manageSettings: "Manage settings",
			nonSendingNote:
				"You're not set up as a sending user yet — ask a Company Admin to make you one if you should send messages yourself.",
			seatsTakenTitle: "All LinkedIn sender seats are taken",
			seatsTakenBody:
				"Every LinkedIn sender seat in your workspace is currently in use, so you can't connect a LinkedIn account right now. You can still connect an email sender below.",
			seatsTakenAskAdmin:
				"A Company Admin manages seats in Settings → Users — ask them to free one up for you.",
			addMoreSeats: "Add more seats",
		},
		extension: {
			title: "Add leads straight from LinkedIn",
			desc: "The free Signalist browser extension puts your lead panel right inside LinkedIn.",
			railNote:
				"One more tool worth grabbing: the Signalist browser extension shows scores and enriched data on any LinkedIn profile and adds people to your lists in one click. Installing takes half a minute.",
			railNoteInvited:
				"Working on LinkedIn a lot? The Signalist browser extension puts your team's lead panel right inside LinkedIn.",
			intro:
				"The extension works right on linkedin.com — no more copy-pasting profiles into Signalist.",
			tileTitle: "Signalist browser extension",
			pillInstalled: "Installed",
			pillNotInstalled: "Not installed",
			benefits: {
				addLeads: "Add any LinkedIn profile to your lists in one click",
				context: "See scores, enriched data and past conversations next to the profile",
				reachOut: "Reach out without leaving LinkedIn",
			},
			installChrome: "Add to Chrome",
			installEdge: "Add to Edge",
			installedNote:
				"The extension is installed — open a LinkedIn profile and the Signalist panel appears right there.",
			afterInstallHint:
				"After installing, open any LinkedIn profile — the Signalist panel appears right there. You may need to sign in once.",
		},
		language: {
			title: "How do you like to write?",
			nextLabel: "Writing languages",
			desc: "The languages you write in shape every AI draft.",
			railNote:
				"Tell us which languages you write in and how you usually open, close and ask — the AI drafts pick it up from here.",
			intro: "This shapes the AI drafts so they sound like you — you can change it any time.",
			saveContinue: "Save & continue",
			none: "None yet",
		},
		deepResearch: {
			title: "Deep Research",
			desc: "AI research digs into every lead before you reach out.",
			railNote:
				"Deep Research reads the web for every lead — funding, hiring, positioning — so your outreach lands with context.",
		},
		knowledge: {
			title: "Your Knowledge Base",
			desc: "What Signalist knows about your company powers every draft.",
			railNote:
				"Your Knowledge Base tells the AI what you sell and how you talk about it. The better it is, the better your drafts.",
		},
		copywriting: {
			title: "Tune your AI copy",
			desc: "Edit real drafts and we'll trace each change back to the input behind it.",
			railNote:
				"Your messages are composed from four inputs — Deep Research, Knowledge Base, Copywriting instructions and Tone of Voice. Edit real drafts, tell us why, and we'll trace each change to the input behind it, then suggest what to tune.",
		},
	},
	howItWorks: {
		stage1: {
			title: "Leads & Signals",
			tabs: {
				signalBased: {
					label: "New & signal-based",
					title: "New leads, surfaced by a signal",
					body: "Be it events and trade shows, LinkedIn posts, engagement, profile visits or job changes — Signalist detects a lot of signals that help you identify the right person at the right time.",
					chips: {
						0: "Event attendees",
						1: "Exhibitors",
						2: "New hires",
						3: "Engaged with content",
						4: "Job changes",
						5: "Profile visits",
						6: "Funding raised",
						7: "Promotions",
					},
				},
				cold: {
					label: "New & cold",
					title: "New leads from cold databases",
					body: "Build fresh lists from the widest possible pool. Pull net-new prospects out of cold databases and let Signalist enrich and qualify them the same way it does your warm leads.",
					chips: {
						0: "LinkedIn Sales Navigator",
						1: "Lookalikes",
						2: "Semantic search",
						3: "Our lead databases",
					},
				},
				tracker: {
					label: "Profile Tracker",
					title: "Nurture accounts you're already watching",
					body: "The Profile Tracker follows your target accounts and existing leads, and the Feed streams every new signal they generate — so a lead you've spoken to before pops back up the moment there's a reason to re-engage.",
					chips: {
						0: "Profile Tracker",
						1: "Job change tracking",
						2: "Custom signals",
						3: "Signal feed",
						4: "Tracked accounts",
					},
				},
				reEngage: {
					label: "Re-engage contacts",
					title: "Re-engage contacts who went quiet",
					body: "A list type of its own. Signalist tracks who replied positively but hasn't been contacted in a while, and pulls them back into view so warm relationships don't slip through the cracks.",
					chips: {
						0: "Replied positively",
						1: "No recent contact",
						2: "Smart re-engage list",
					},
				},
			},
		},
		stage2: {
			title: "Segment & score",
			tabs: {
				scoring: {
					label: "Lead scoring",
					title: "Lead scoring",
					body: "Every lead is scored on four inputs — your Company Segments, Persona Segments, firmographic data, and AI research — and sorted into a clear priority.",
					link: "Review lead scoring",
					legend: {
						p1: {
							badge: "P1",
							name: "Priority 1",
							desc: "Fits the scoring criteria best",
						},
						p2: {
							badge: "P2",
							name: "Priority 2",
							desc: "Strong fit on most criteria",
						},
						p3: {
							badge: "P3",
							name: "Priority 3",
							desc: "Partial fit — worth a lighter touch",
						},
						dq: {
							badge: "DQ",
							name: "Disqualified",
							desc: "Filtered out by the criteria",
						},
					},
				},
				companySegments: {
					label: "Company segments",
					title: "Company segments",
					body: "We categorize the companies on your lead lists into your distinct ICP segments, so you can filter easily and apply different scorings to each.",
					chips: {
						0: "Tech Startups & Scale-ups",
						1: "Fintech & Financial Technology",
						2: "European SMEs in Digital Sectors",
						3: "E-commerce & Online Retail",
					},
				},
				personaSegments: {
					label: "Persona segments",
					title: "Persona segments",
					body: "We categorize the people on your lead lists into your distinct persona segments, so you can filter easily and apply different scorings to each.",
					chips: {
						0: "CEO, MD, Founder & Board",
						1: "Finance & Accounting Lead",
						2: "Operations Lead",
						3: "People & HR Lead",
					},
				},
			},
		},
		stage3: {
			title: "Reach out",
			tabs: {
				aiDraft: {
					label: "AI Draft",
					title: "Let AI draft the whole sequence",
					body: "AI writes a full multi-step sequence for you, combining four inputs:",
					ingredients: {
						leadData: {
							name: "Enriched lead data",
							desc: "Employee size, location, languages, role & more — the facts that make a message land.",
						},
						deepResearch: {
							name: "Deep Research",
							desc: "Talking points and why this person should talk to you right now.",
						},
						knowledge: {
							name: "Your Knowledge Base",
							desc: "The pain points you solve and the value proposition you lead with.",
						},
						tone: {
							name: "Your tone of voice",
							desc: "Learns from how you edit AI drafts and which messages earn positive replies.",
						},
					},
				},
				campaigns: {
					label: "Campaigns",
					title: "Multi-channel campaigns",
					body: "Drop leads into a sequenced campaign across email and LinkedIn. Keep one structure while personalizing every step per lead with three tools:",
					tools: {
						variables: {
							name: "Variables",
							desc: "Auto-fill fields like {first_name} or {company} from enriched data.",
						},
						spintax: {
							name: "Spintax",
							desc: "Rotate phrasings — {Hi|Hey|Hello} — so no two messages read identically.",
						},
						snippets: {
							name: "AI Snippets",
							desc: "Generate a tailored opener or hook per lead, on the fly.",
						},
					},
				},
				manual: {
					label: "Write manually",
					title: "Write manually",
					body: "Prefer a personal touch? Open any lead and write the message yourself. All the enriched data and Deep Research sit right beside the composer, so you have the full context without leaving the screen.",
				},
			},
		},
	},
};
